import { Box, Skeleton } from '@chakra-ui/react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import { FC, Fragment, ReactNode } from 'react';

import { AddToCalendar } from '@/components/ui/shared/buttons/AddToCalendar/AddToCalendar';
import useSession from '@/hooks/useSession';
import { useResources } from '@/lib/datx-jsonapi-react';
import { queryProfileTimezones } from '@/queries/profile-timezones';
import { ThinEvent } from '@/resources/ThinEvent';
import { ChakraTheme } from '@/styles/themes/default';
import { IThemeProps } from '@/styles/themes/types';
import { dateToString, formatConferenceDate } from '@/utils/date';
import { useUserEventTimezone } from '@/hooks/useUserEventTimezone';

const EventDurationWithTimezone = dynamic(
  () =>
    import('@/components/ui/shared/event/EventDurationWithTimezone/EventDurationWithTimezone').then(
      (mod) => mod.EventDurationWithTimezone
    ),
  {
    ssr: false,
  }
);

const DateContainer = styled.div<IThemeProps>`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.legacy.colors.brand.signalRed};
  & > *:not(:last-child) {
    margin-right: 2ch;
  }
`;

const eventTimeStyles = (theme: ChakraTheme) => css`
  display: inline-block;
  margin: 0;
  color: ${theme.legacy.colors.brand.signalRed};
  line-height: 40px;
`;

interface IEventInfoProps {
  event: ThinEvent;
  eventTicketSlot?: ReactNode;
}

export const EventInfo: FC<IEventInfoProps> = ({ event, eventTicketSlot, ...restProps }) => {
  const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const userEventTimezone = useUserEventTimezone(event?.id);
  const selectedTimezone = userEventTimezone || defaultTimezone;

  const formattedDuration = formatConferenceDate(event, selectedTimezone);

  return (
    <div {...restProps}>
      {event ? (
        <Fragment>
          <DateContainer>
            <EventDurationWithTimezone event={event} fontSize={['2xl']} css={eventTimeStyles} />

            <Box w={10} h={10} position="relative">
              <AddToCalendar
                name={event.name}
                color="almostRed.500"
                startUtc={dateToString(event.startsAt, 'LL/dd/yyyy HH:mm')}
                endUtc={dateToString(event.endsAt, 'LL/dd/yyyy HH:mm')}
                description={event.shortDescription || formattedDuration}
                size={40}
                iconSize={24}
                tooltipPlacement="end"
              />
            </Box>
          </DateContainer>
          {eventTicketSlot}
        </Fragment>
      ) : (
        <Skeleton css={{ width: 350, height: 30, marginBottom: 20 }} />
      )}
    </div>
  );
};
